/**
 * Multitrack timeline canvas.
 * Design docs -> frontend/src/features/flightSchedule/ScheduleDraft/docs/20_workspace_ux/20_multitrack_view.md
 * Design docs -> frontend/src/features/flightSchedule/ScheduleDraft/docs/20_workspace_ux/28_frontend_code_map_and_data_flow.md
 */

import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Empty, Typography } from "antd";
import "vis-timeline/styles/vis-timeline-graph2d.css";
import "./MultitrackTimelineCanvas.css";
import { createTimelineAdapter } from "./timelineAdapter";
import { debugScheduleDraftTimeline } from "../lib/timelineDebug";

const { Text } = Typography;

const MultitrackTimelineCanvas = forwardRef(({ model, onSelectItem, onLayoutChange, onHoverItem, onTrackAction, viewportBinding, itemPreviewTrigger }, ref) => {
    const containerRef = useRef(null);
    const adapterRef = useRef(null);
    const onSelectItemRef = useRef(onSelectItem);
    const onLayoutChangeRef = useRef(onLayoutChange);
    const onHoverItemRef = useRef(onHoverItem);
    const onTrackActionRef = useRef(onTrackAction);
    const viewportBindingRef = useRef(viewportBinding);
    const itemPreviewTriggerRef = useRef(itemPreviewTrigger);
    const hasVisualGroups = Boolean(model?.tracks?.length || model?.contextLanes?.length);

    // Keep the vis instance stable; Compare match refetches should update data, not recreate the root node.
    useEffect(() => {
        onSelectItemRef.current = onSelectItem;
        onLayoutChangeRef.current = onLayoutChange;
        onHoverItemRef.current = onHoverItem;
        onTrackActionRef.current = onTrackAction;
        viewportBindingRef.current = viewportBinding;
        itemPreviewTriggerRef.current = itemPreviewTrigger;
    });

    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }

        adapterRef.current = createTimelineAdapter({
            container: containerRef.current,
            onSelectItem: (item) => onSelectItemRef.current?.(item),
            onLayoutChange: (event) => onLayoutChangeRef.current?.(event),
            onHoverItem: (item, hoverContext) => onHoverItemRef.current?.(item, hoverContext),
            onTrackAction: (action, track) => onTrackActionRef.current?.(action, track),
            itemPreviewTrigger: itemPreviewTriggerRef.current,
            onRangeChange: (event) => {
                const binding = viewportBindingRef.current;
                if (!binding?.id || typeof binding.onRangeChange !== "function") {
                    return;
                }

                const { onRangeChange, id, groupId, meta, ...viewportMeta } = binding;
                onRangeChange({
                    ...event,
                    viewportId: id,
                    groupId,
                    viewportMeta: meta || viewportMeta,
                });
            },
        });

        debugScheduleDraftTimeline("canvas-mount", {
            hasContainer: true,
        });

        return () => {
            onHoverItemRef.current?.(null);
            adapterRef.current?.destroy();
            adapterRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!containerRef.current || !adapterRef.current) {
            return undefined;
        }

        const requestHostLayoutRefresh = (source) => {
            debugScheduleDraftTimeline("canvas-host-layout-change", {
                source,
                clientWidth: containerRef.current?.clientWidth || 0,
                clientHeight: containerRef.current?.clientHeight || 0,
                hasVisualGroups,
            });
            adapterRef.current?.refreshLayout(source);
        };

        const resizeObserver = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(() => {
                  requestHostLayoutRefresh("resize-observer");
              })
            : null;

        resizeObserver?.observe(containerRef.current);

        const handleWindowResize = () => {
            requestHostLayoutRefresh("window-resize");
        };

        window.addEventListener("resize", handleWindowResize);

        return () => {
            window.removeEventListener("resize", handleWindowResize);
            resizeObserver?.disconnect();
        };
    }, [hasVisualGroups]);

    useEffect(() => {
        debugScheduleDraftTimeline("canvas-render", {
            hasAdapter: Boolean(adapterRef.current),
            hasVisualGroups,
            trackCount: model?.tracks?.length || 0,
            contextLaneCount: model?.contextLanes?.length || 0,
            itemCount: model?.items?.length || 0,
            timeWindow: model?.timeWindow,
        });

        if (!adapterRef.current) {
            return;
        }

        if (!hasVisualGroups) {
            onHoverItemRef.current?.(null);
            adapterRef.current.clear();
            return;
        }

        adapterRef.current.render(model);
    }, [hasVisualGroups, model]);

    useImperativeHandle(ref, () => ({
        zoomIn: () => adapterRef.current?.zoomIn(),
        zoomOut: () => adapterRef.current?.zoomOut(),
        fit: () => adapterRef.current?.fit(),
        redraw: () => adapterRef.current?.redraw(),
        clearPreview: () => adapterRef.current?.clearPreview(),
        setWindow: (range, options) => adapterRef.current?.setWindow(range, options),
        getWindow: () => adapterRef.current?.getWindow(),
    }));

    return (
        <div className="schedule-draft-timeline-shell">
            <div ref={containerRef} className="schedule-draft-timeline-container" />
            {!hasVisualGroups ? (
                <div className="schedule-draft-timeline-empty-overlay">
                    <div className="schedule-draft-timeline-empty-state">
                        <Empty description={model?.emptyState?.title || "当前航班计划尚无可展示轨道"} image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        {model?.emptyState?.description ? <Text type="secondary">{model.emptyState.description}</Text> : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
});

MultitrackTimelineCanvas.displayName = "MultitrackTimelineCanvas";

export default MultitrackTimelineCanvas;
